import { useState, useEffect, useCallback, useRef } from "react";
import i18n from "i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { apiClient, RemainingSwipesResponse } from "../lib/apiClient.js";

// Cross-component dedupe: multiple pages can mount `useSwipeLimits()` at once.
let inFlightRemainingSwipes: Promise<RemainingSwipesResponse> | null = null;
let inFlightStartedAtMs = 0;

interface SwipeLimitsState extends RemainingSwipesResponse {
  loading: boolean;
  error: string | null;
}

interface UseSwipeLimitsReturn extends SwipeLimitsState {
  refetch: () => Promise<RemainingSwipesResponse | null>;
  setRemainingSwipes: (remaining: number) => void;
  consumeSwipe: () => void;
}

/**
 * Hook to fetch and manage swipe limits for the current user
 * Automatically refetches every 5 minutes and provides manual refetch capability
 */
export const useSwipeLimits = (): UseSwipeLimitsReturn => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const hasLoadedOnceRef = useRef(false);

  const [state, setState] = useState<SwipeLimitsState>({
    tier: "free",
    tierDisplayName: "Explorador",
    // Start as null to avoid a misleading "default" value (e.g. 20)
    // that could trigger PawMatch fetches before we know the real limits.
    maxDaily: null,
    usedToday: 0,
    remainingSwipes: null,
    resetsAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    loading: true,
    error: null,
  });

  const fetchSwipeLimits = useCallback(async () => {
    if (!isAuthenticated) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: i18n.t("errors:auth.notAuthenticated"),
      }));
      return null;
    }

    try {
      setState((prev) => ({
        ...prev,
        // Keep existing UI stable during refetches after the first completed fetch.
        loading: !hasLoadedOnceRef.current,
        error: null,
      }));

      // Get auth token
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });

      const authHeaders = {
        Authorization: `Bearer ${token}`,
      };

      // If a fetch is already in-flight (e.g. multiple components mounted),
      // await it instead of creating bursts of concurrent requests.
      const now = Date.now();
      const canReuseInFlight =
        inFlightRemainingSwipes && now - inFlightStartedAtMs < 2500;

      if (!canReuseInFlight) {
        inFlightStartedAtMs = now;
        inFlightRemainingSwipes = apiClient
          .getRemainingSwipes(authHeaders)
          .finally(() => {
            inFlightRemainingSwipes = null;
          });
      }

      const data =
        await (inFlightRemainingSwipes as Promise<RemainingSwipesResponse>);

      setState({
        tier: data.tier,
        tierDisplayName: data.tierDisplayName,
        maxDaily: data.maxDaily,
        usedToday: data.usedToday,
        remainingSwipes: data.remainingSwipes,
        resetsAt: data.resetsAt,
        streak: data.streak,
        loading: false,
        error: null,
      });
      hasLoadedOnceRef.current = true;
      return data;
    } catch (error) {
      console.error("Erro ao buscar limites de swipes:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: i18n.t("errors:pawmatch.loadSwipeLimitsFailed"),
      }));
      hasLoadedOnceRef.current = true;
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchSwipeLimits();
    }
  }, [isAuthenticated, fetchSwipeLimits]);

  // Auto-refetch every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(
      () => {
        fetchSwipeLimits();
      },
      5 * 60 * 1000,
    ); // 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchSwipeLimits]);

  // Method to update remaining swipes directly (used for immediate UI updates from interaction response)
  const setRemainingSwipes = useCallback((remaining: number) => {
    setState((prev) => ({
      ...prev,
      remainingSwipes: remaining,
      usedToday:
        typeof prev.maxDaily === "number"
          ? prev.maxDaily - remaining
          : prev.usedToday,
    }));
  }, []);

  // Fallback optimistic updater for cases where interaction response omits remainingSwipes.
  const consumeSwipe = useCallback(() => {
    setState((prev) => {
      if (typeof prev.maxDaily !== "number" || prev.maxDaily <= 0) {
        return prev;
      }

      const nextRemaining =
        typeof prev.remainingSwipes === "number"
          ? Math.max(0, prev.remainingSwipes - 1)
          : Math.max(0, prev.maxDaily - (prev.usedToday + 1));
      const nextUsed = Math.min(prev.maxDaily, prev.usedToday + 1);

      return {
        ...prev,
        remainingSwipes: nextRemaining,
        usedToday: nextUsed,
      };
    });
  }, []);

  return {
    ...state,
    refetch: fetchSwipeLimits,
    setRemainingSwipes,
    consumeSwipe,
  };
};
